import React, { FC, useEffect, useState } from 'react'

import { alpha, Table as MuiTable } from '@mui/material'
import { TableSortLabel, useTheme } from '@mui/material'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import { motion } from 'framer-motion'
import { omit } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { disableMassFill, dismissAllTradesSelect } from '../../pages/TradingLog'
import TradeAiAnalysisModal from '../../pages/TradingLog/TradeAiAnalysisModal'
import { APP_PATHS } from '../../routes/paths'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useThemeMode } from '../../theme/ThemeModeContext'
import { SortDirection } from '../../types'
import { useIsMobile } from '../../utils/useIsMobile'

import { JournalingModal } from './JournalingModal'
import { TableBodyRow } from './TableBodyRow'
import { TableHeadCell } from './TableHeadCell'
import { TradingLogHeaderCellSelect } from './TradingLogHeaderCellSelect'
import { AvailableTableTypes, TableProps } from './types'

const Table: FC<TableProps<AvailableTableTypes>> = ({
  columnOrder,
  sortingColumn,
  sortingDirection,
  onColumnReorder,
  onSort,
  displayedTrades,
  keyForRefresh,
  tableStyles,
  showTradeInfoColumn = true,
  showAiAnalysisColumn = true,
  onAnalysisSaved,
}) => {
  const massFillEnabled = useAppSelector((state) => state.tradingLog.massFillEnabled)
  const { spacing, palette } = useTheme()
  const { resolvedMode } = useThemeMode()

  const headerColor =
    resolvedMode === 'dark' ? alpha(palette.background.default, 0.5) : palette.blue[100]

  const isMobile = useIsMobile('md')

  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)

  const { t } = useTranslation()
  const [selectedRow, setSelectedRow] = useState<string | null>(null)

  const [analysisRowId, setAnalysisRowId] = useState<string | null>(null)
  const [analysisLoadingMap, setAnalysisLoadingMap] = useState<Record<number, boolean>>({})
  const analysisLoading = !!analysisRowId && analysisLoadingMap[Number(analysisRowId)] === true

  const location = useLocation()
  const dispatch = useAppDispatch()
  const isTradingLogTable = location.pathname === APP_PATHS.tradingLog
  const isSettingsTable = location.pathname === APP_PATHS.settings

  useEffect(() => {
    dispatch(disableMassFill())
    dispatch(dismissAllTradesSelect())
  }, [location, currentAccount])

  const isJournalingModalOpen = !!selectedRow || massFillEnabled

  const onCloseJournalingModal = () => {
    dispatch(disableMassFill())
    setSelectedRow(null)
  }

  const onSetAnalysisRowLoading = (tradeId: number, isLoading: boolean) => {
    setAnalysisLoadingMap((prev) => {
      if (isLoading) {
        return { ...prev, [tradeId]: true }
      } else {
        return omit(prev, tradeId)
      }
    })
  }

  const openAiAnalysisModal = (tradeId: string | null) => {
    setAnalysisRowId(tradeId)
  }

  const closeAnalysisModal = () => {
    setAnalysisRowId(null)
  }

  return (
    <TableContainer sx={{ flexGrow: 1, ...tableStyles }}>
      <MuiTable sx={isSettingsTable ? { tableLayout: 'fixed', width: '100%' } : undefined}>
        <TableHead>
          <motion.tr layout style={{ backgroundColor: headerColor, borderRadius: spacing(0.5) }}>
            {isTradingLogTable && <TradingLogHeaderCellSelect />}
            {showAiAnalysisColumn && <TableHeadCell>{t('aiTableCell.label')}</TableHeadCell>}
            {showTradeInfoColumn && <TableHeadCell>{t('shareTrade', 'Share trade')}</TableHeadCell>}

            {columnOrder.map((columnKey, index) => (
              <motion.th
                key={`${columnKey}${keyForRefresh}${columnOrder.join()}`}
                layout
                drag="x"
                dragSnapToOrigin
                layoutId={`column-${columnKey}${keyForRefresh}`}
                onDragEnd={(event, info) => {
                  const dragOffset = info.offset.x
                  const dragIndex = Math.floor(dragOffset / 150)
                  onColumnReorder(index, index + dragIndex)
                }}
                style={{
                  color: palette.grey[700],
                  fontWeight: 800,
                  fontSize: isMobile ? 12 : 14,
                  textAlign: 'right',
                  padding: spacing(1.125),
                  verticalAlign: 'top',
                }}
              >
                <TableSortLabel
                  active={sortingColumn === columnKey}
                  direction={sortingDirection.toLowerCase() as SortDirection}
                  onClick={() => onSort(columnKey as keyof AvailableTableTypes)}
                >
                  {t(columnKey)}
                </TableSortLabel>
              </motion.th>
            ))}
          </motion.tr>
        </TableHead>

        <TableBody>
          {displayedTrades.map((logEntry, i) => {
            const rawEntryId = 'id' in logEntry ? logEntry.id : null
            const entryId =
              rawEntryId !== null && rawEntryId !== undefined ? String(rawEntryId) : null

            const analysisLoading =
              rawEntryId !== null &&
              rawEntryId !== undefined &&
              analysisLoadingMap[Number(rawEntryId)] === true

            return (
              <TableBodyRow
                key={entryId ?? i}
                logEntry={logEntry}
                columnOrder={columnOrder as (keyof AvailableTableTypes)[]}
                onRowClick={() => setSelectedRow(entryId)}
                selectedRow={entryId}
                displaySelectTradingLogRow={isTradingLogTable}
                showTradeInfoColumn={showTradeInfoColumn}
                showAiAnalysisColumn={showAiAnalysisColumn}
                onAiAnalysis={() => openAiAnalysisModal(entryId)}
                analysisLoading={analysisLoading}
              />
            )
          })}
        </TableBody>
      </MuiTable>

      <JournalingModal
        tradingLogId={selectedRow}
        isOpen={isJournalingModalOpen}
        onClose={onCloseJournalingModal}
      />
      <TradeAiAnalysisModal
        tradeId={analysisRowId ? Number(analysisRowId) : null}
        open={Boolean(analysisRowId)}
        onClose={closeAnalysisModal}
        onAnalysisSaved={onAnalysisSaved}
        loading={analysisLoading}
        onSetLoading={onSetAnalysisRowLoading}
      />
    </TableContainer>
  )
}

export default Table
